import { FirebaseError } from 'firebase/app';
import type { AuthErrorCodes } from 'firebase/auth';
import { PASSWORD_REQUIREMENTS_MESSAGE, SUPPORT_EMAIL } from './accountConstants';

/**
 * Where the error happened. A wrong password on sign-in shouldn't reveal whether the email
 * exists; in Account Settings the user is already signed in, so we can be specific.
 */
export type AuthErrorContext = 'signIn' | 'account';

/**
 * Codes are written as strings but type-checked against Firebase's list. Importing the
 * `AuthErrorCodes` value instead would keep its full ~100-entry map in the main bundle.
 */
type AuthErrorCode =
  | (typeof AuthErrorCodes)[keyof typeof AuthErrorCodes]
  // Not in AuthErrorCodes in firebase/auth v10.
  | 'auth/password-does-not-meet-requirements';

const CREDENTIAL_ERRORS: ReadonlySet<string> = new Set<AuthErrorCode>([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
]);

/** Errors where the user simply backed out (e.g. closed the Google popup). Show nothing. */
const SILENT_ERRORS: ReadonlySet<string> = new Set<AuthErrorCode>([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

const AUTH_ERROR_MESSAGES: Partial<Record<AuthErrorCode, string>> = {
  'auth/too-many-requests': 'Too many attempts. Try again in a few minutes.',
  'auth/weak-password': PASSWORD_REQUIREMENTS_MESSAGE,
  'auth/password-does-not-meet-requirements': PASSWORD_REQUIREMENTS_MESSAGE,
  'auth/email-already-in-use':
    'That email already has an account. Log in instead, or use "Log in with Google" if you signed up that way.',
  'auth/account-exists-with-different-credential':
    'An account with this email already exists. Log in with the method you used to sign up.',
  'auth/credential-already-in-use': 'That sign-in method is already attached to another account.',
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/user-disabled': `This account has been deactivated. Contact ${SUPPORT_EMAIL} to reopen it.`,
  'auth/requires-recent-login': 'For your security, sign in again and then retry.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/popup-blocked': 'Allow pop-ups for this site to continue with Google.',
  'auth/user-mismatch': 'Use the Google account linked to your Wander account.',
};

/**
 * Our Cloud Functions write their HttpsError messages for these codes to be shown to users
 * (e.g. "Sign in again to deactivate your account."), so they pass through as-is.
 */
const USER_FACING_FUNCTION_ERRORS: ReadonlySet<string> = new Set([
  'functions/not-found',
  'functions/permission-denied',
  'functions/resource-exhausted',
  'functions/failed-precondition',
  'functions/invalid-argument',
]);

/**
 * Maps a Firebase Auth or Cloud Function error to user-facing copy.
 * Returns `null` when nothing should be shown, and `fallback` for unknown errors.
 */
export const getAuthErrorMessage = (
  error: unknown,
  fallback: string,
  context: AuthErrorContext = 'account'
): string | null => {
  if (!(error instanceof FirebaseError)) {
    return fallback;
  }
  if (USER_FACING_FUNCTION_ERRORS.has(error.code)) {
    return error.message;
  }
  if (SILENT_ERRORS.has(error.code)) {
    return null;
  }
  if (CREDENTIAL_ERRORS.has(error.code)) {
    return context === 'signIn'
      ? 'Email or password is incorrect.'
      : 'Current password is incorrect.';
  }
  return AUTH_ERROR_MESSAGES[error.code as AuthErrorCode] ?? fallback;
};
